import { useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw, ScanBarcode } from 'lucide-react';
import { api, apiError } from '@/lib/pos-api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const PAGE_SIZE = 20;
const status = value => ({draft: 'Draf', submitted: 'Menunggu audit', posted: 'Sudah diposting'}[value] || value);
const date = value => value ? new Date(value).toLocaleString('id-ID') : '—';
const number = value => Number(value || 0).toLocaleString('id-ID');

// AppLayout keys this component by user, tenant and store: no cross-scope cached data.
export default function PdaNotifications({ storeId }) {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const update = () => setRefresh(value => value + 1);
    const visible = () => { if (!document.hidden) update(); };
    const timer = window.setInterval(visible, 30000);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      document.removeEventListener('visibilitychange', visible);
    };
  }, []);

  useEffect(() => {
    if (!storeId) return;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    async function load() {
      try {
        if (!navigator.onLine) throw new Error('Sedang offline. Sambungkan internet untuk memperbarui draf PDA.');
        const { data } = await api.get('/pda-notifications', {
          headers: { 'X-Store-Id': storeId }, params: { offset, limit: PAGE_SIZE }, signal: controller.signal,
        });
        if (!active) return;
        setList(data); setError('');
        if (offset && offset >= data.total) setOffset(0);
      } catch (e) {
        if (active) setError(apiError(e));
      } finally { if (active) setLoading(false); }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [storeId, offset, refresh]);

  useEffect(() => {
    if (!selected || !open || !storeId) return;
    const controller = new AbortController();
    let active = true;
    setDetailLoading(true); setDetailError(''); setDetail(null);
    api.get(`/pda-notifications/${selected}`, { headers: { 'X-Store-Id': storeId }, signal: controller.signal })
      .then(({ data }) => { if (active) setDetail(data); })
      .catch(e => { if (active) setDetailError(apiError(e)); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [selected, open, storeId, refresh]);

  const changeOpen = value => {
    setOpen(value); setSelected(null); setDetail(null);
    if (value) { setOffset(0); setRefresh(n => n + 1); }
  };
  return <Dialog open={open} onOpenChange={changeOpen}>
    <DialogTrigger asChild>
      <button type="button" data-testid="pda-notifications" className="theme-shortcut relative shrink-0" disabled={!storeId}
        aria-label={`Draf PDA${list ? `: ${list.total} belum selesai` : ''}${error ? ', perlu diperbarui' : ''}`} title="Draf stok opname dari PDA">
        <ScanBarcode size={19} aria-hidden="true" />
        {(error || list?.total > 0) && <span data-testid="pda-notification-count" className="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full bg-warning-readable text-white text-[10px] leading-4 text-center">{error ? '!' : list.total > 99 ? '99+' : list.total}</span>}
      </button>
    </DialogTrigger>
    <DialogContent className="w-[calc(100%-1rem)] max-w-2xl max-h-[85dvh] flex flex-col gap-3 p-4 sm:p-5" data-testid="pda-notifications-dialog">
      <div className="pr-7">
        <DialogTitle>Draf PDA · Stok opname</DialogTitle>
        <DialogDescription className="mt-1">Draf yang sudah dikirim ke POS untuk cabang aktif. Membuka rincian tidak mengubah stok.</DialogDescription>
      </div>
      <div className="flex items-center justify-between gap-2">
        {selected ? <Button variant="outline" size="sm" onClick={() => { setSelected(null); setDetail(null); }}><ArrowLeft size={16} className="mr-1" />Daftar draf</Button>
          : <span className="text-sm text-ui-muted" role="status">{list ? `${list.total} dokumen belum selesai` : 'Memuat draf…'}</span>}
        <Button variant="outline" size="sm" data-testid="pda-notifications-refresh" disabled={loading || detailLoading} onClick={() => setRefresh(n => n + 1)}><RefreshCw size={15} className="mr-1" />Perbarui</Button>
      </div>
      {error && <p role="alert" className="text-sm text-danger-readable">{error} {list && 'Jumlah yang ditampilkan adalah data terakhir.'}</p>}
      <div className="min-h-0 overflow-y-auto overscroll-contain space-y-2" aria-busy={selected ? detailLoading : loading}>
        {!selected && <>
          {!loading && !error && list?.total === 0 && <p className="py-6 text-center text-sm text-ui-muted">Belum ada draf stok opname PDA yang menunggu.</p>}
          {list?.items.map(item => <button key={item.id} type="button" data-testid={`pda-draft-${item.id}`} onClick={() => setSelected(item.id)} className="w-full rounded-lg border border-ui-line p-3 text-left hover:bg-ui-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
            <div className="flex flex-wrap items-center justify-between gap-1"><strong className="text-sm break-all">{item.request_no}</strong><span className="text-xs rounded bg-warning-soft text-warning-readable px-2 py-1">{status(item.state)}</span></div>
            <p className="text-xs text-ui-muted mt-1 break-words">{item.item_count} produk · {item.last_by || item.by} · {date(item.updated_at)}</p>
          </button>)}
        </>}
        {selected && detailLoading && <p className="text-sm py-4" role="status">Memuat rincian…</p>}
        {selected && detailError && <p role="alert" className="text-sm text-danger-readable">{detailError}</p>}
        {selected && detail && <div data-testid="pda-notification-detail" className="space-y-3">
          <div className="text-sm break-words"><strong>{detail.request_no}</strong><p>{status(detail.state)} · Revisi {detail.revision}</p><p className="text-ui-muted">{detail.last_by || detail.by} · {date(detail.updated_at)}</p>{detail.note && <p className="mt-2 whitespace-pre-wrap">Catatan: {detail.note}</p>}</div>
          {detail.items.map(item => <div key={item.product_id} className="rounded-lg border border-ui-line p-3">
            <p className="font-semibold text-sm break-words">{item.name}</p><p className="text-xs text-ui-muted break-all">{item.barcode || 'Tanpa barcode'} · Satuan dasar: {item.unit}</p>
            <dl className="grid grid-cols-3 gap-2 text-sm mt-2"><div><dt className="text-xs text-ui-muted">Stok saat hitung</dt><dd>{number(item.before)}</dd></div><div><dt className="text-xs text-ui-muted">Hasil hitung</dt><dd>{number(item.counted)}</dd></div><div><dt className="text-xs text-ui-muted">Selisih</dt><dd>{item.diff > 0 ? '+' : ''}{number(item.diff)}</dd></div></dl>
          </div>)}
          <p className="text-xs text-ui-muted">Hanya lihat. Audit dan posting tetap dilakukan oleh admin melalui alur stok opname PDA.</p>
        </div>}
      </div>
      {!selected && list?.total > PAGE_SIZE && <div className="flex items-center justify-between gap-2 text-xs">
        <Button variant="outline" size="sm" disabled={loading || offset === 0} onClick={() => setOffset(n => Math.max(0, n - PAGE_SIZE))}>Sebelumnya</Button>
        <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, list.total)}</span>
        <Button variant="outline" size="sm" disabled={loading || offset + PAGE_SIZE >= list.total} onClick={() => setOffset(n => n + PAGE_SIZE)}>Berikutnya</Button>
      </div>}
      <p className="text-xs text-ui-muted">Draf yang masih tersimpan hanya di perangkat PDA belum tampil di sini. Daftar diperbarui otomatis setiap 30 detik.</p>
    </DialogContent>
  </Dialog>;
}
